import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { HardHat, Loader2, UserPlus, Mail, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { Separator } from "@/components/ui/separator";
import { api } from "@/lib/api";
import { isFacebookConfigured, loadFacebookSdk, facebookLogin } from "@/lib/facebook";
import InstallPWA from "@/components/InstallPWA";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function googleLogin() {
  const redirectUrl = window.location.origin + "/";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [fbBusy, setFbBusy] = useState(false);
  const [fbReady, setFbReady] = useState(false);
  const [regOpen, setRegOpen] = useState(false);
  const [regForm, setRegForm] = useState({ name: "", email: "", password: "" });
  const [regBusy, setRegBusy] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotStep, setForgotStep] = useState(1);
  const [forgotForm, setForgotForm] = useState({ email: "", code: "", new_password: "", new_password2: "" });
  const [forgotBusy, setForgotBusy] = useState(false);
  const fbEnabled = isFacebookConfigured();
  const { login, setUserAndToken } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    if (!fbEnabled) return;
    loadFacebookSdk().then(() => setFbReady(true)).catch(() => setFbReady(false));
  }, [fbEnabled]);

  const onFacebook = async () => {
    setFbBusy(true);
    try {
      const token = await facebookLogin();
      const { data } = await api.post("/auth/facebook/session", { access_token: token });
      if (data?.access_token) localStorage.setItem("access_token", data.access_token);
      if (setUserAndToken) setUserAndToken(data.user, data.access_token);
      toast.success("Accesso Facebook riuscito");
      nav("/");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Accesso Facebook fallito");
    } finally {
      setFbBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const res = await login(email, password);
    setBusy(false);
    if (res.ok) {
      toast.success("Accesso effettuato");
      nav("/");
    } else {
      toast.error(res.error || "Credenziali non valide");
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex relative overflow-hidden brand-grid">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-accent/10" />
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-xl bg-primary flex items-center justify-center">
              <HardHat className="h-6 w-6 text-primary-foreground" />
            </div>
            <div>
              <div className="font-bold text-2xl tracking-tight">Cantiere360</div>
              <div className="text-xs uppercase tracking-widest text-muted-foreground">Gestionale Edile Pro</div>
            </div>
          </div>
          <div className="space-y-4 max-w-md">
            <h1 className="text-5xl font-extrabold tracking-tight leading-[1.05]">
              Dal <span className="text-primary">cantiere</span> alla contabilità, tutto in un posto.
            </h1>
            <p className="text-muted-foreground text-lg">
              Presenze operai, rendiconti giornalieri, fatture con OCR e IVA automatica, report AI
              per vedere ogni cantiere in tempo reale.
            </p>
          </div>
          <div className="text-xs text-muted-foreground">
            © Cantiere360 · fatto per imprese edili italiane
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 md:p-12">
        <Card className="w-full max-w-md card-elevated">
          <CardContent className="p-8 space-y-6">
            <div className="space-y-2">
              <div className="lg:hidden flex items-center gap-2 mb-4">
                <HardHat className="h-5 w-5 text-primary" />
                <span className="font-bold text-xl">Cantiere360</span>
              </div>
              <h2 className="text-3xl font-bold tracking-tight">Accedi al tuo account</h2>
              <p className="text-sm text-muted-foreground">Usa le credenziali fornite dalla tua azienda.</p>
            </div>
            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input id="email" data-testid="login-email" type="email" value={email}
                       onChange={(e) => setEmail(e.target.value)} required autoFocus />
              </div>
              <div className="space-y-2">
                <Label htmlFor="pwd">Password</Label>
                <Input id="pwd" data-testid="login-password" type="password" value={password}
                       onChange={(e) => setPassword(e.target.value)} required />
              </div>
              <Button data-testid="login-submit" type="submit" disabled={busy} className="w-full h-11">
                {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Accedi
              </Button>
            </form>

            <div className="relative">
              <Separator />
              <span className="absolute left-1/2 -translate-x-1/2 -top-2.5 px-2 bg-card text-[10px] uppercase tracking-widest text-muted-foreground">
                oppure
              </span>
            </div>

            <div className="space-y-2">
              <Button
                data-testid="google-login"
                type="button"
                variant="outline"
                onClick={googleLogin}
                className="w-full h-11 font-semibold"
              >
                <svg className="h-5 w-5 mr-2" viewBox="0 0 48 48" aria-hidden="true">
                  <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.9 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.4-.4-3.5z"/>
                  <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.6 8.3 6.3 14.7z"/>
                  <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.1 35.1 26.7 36 24 36c-5.3 0-9.7-3.1-11.3-7.6l-6.5 5C9.4 39.6 16.1 44 24 44z"/>
                  <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4.1 5.5l6.2 5.2C41 36.5 44 30.7 44 24c0-1.2-.1-2.4-.4-3.5z"/>
                </svg>
                Accedi con Google
              </Button>

              {fbEnabled && (
                <Button
                  data-testid="facebook-login"
                  type="button"
                  variant="outline"
                  disabled={!fbReady || fbBusy}
                  onClick={onFacebook}
                  className="w-full h-11 font-semibold"
                >
                  {fbBusy ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24" aria-hidden="true">
                      <path fill="#1877F2" d="M24 12.07C24 5.4 18.63 0 12 0S0 5.4 0 12.07c0 6 4.39 10.98 10.13 11.88v-8.4H7.08v-3.48h3.05V9.41c0-3.02 1.79-4.68 4.53-4.68 1.31 0 2.68.24 2.68.24v2.96h-1.51c-1.49 0-1.96.93-1.96 1.88v2.26h3.33l-.53 3.48h-2.8V24C19.61 23.05 24 18.07 24 12.07z"/>
                    </svg>
                  )}
                  Accedi con Facebook
                </Button>
              )}
            </div>

            <div className="pt-4 border-t border-border">
              <InstallPWA className="w-full" variant="outline" size="default" label="Installa app sul telefono" />
              <div className="text-center mt-3 flex items-center justify-center gap-3 flex-wrap">
                <button
                  type="button"
                  data-testid="open-forgot"
                  onClick={() => { setForgotStep(1); setForgotForm({ email: email || "", code: "", new_password: "", new_password2: "" }); setForgotOpen(true); }}
                  className="text-xs text-primary hover:underline font-semibold inline-flex items-center gap-1"
                >
                  <KeyRound className="h-3 w-3" /> Password dimenticata?
                </button>
                <span className="text-xs text-muted-foreground">·</span>
                <button
                  type="button"
                  data-testid="open-register"
                  onClick={() => setRegOpen(true)}
                  className="text-xs text-primary hover:underline font-semibold inline-flex items-center gap-1"
                >
                  <UserPlus className="h-3 w-3" /> Nuovo? Richiedi account
                </button>
              </div>
            </div>

            <Dialog open={regOpen} onOpenChange={(o) => { setRegOpen(o); if (!o) { setRegForm({ name: "", email: "", password: "" }); } }}>
              <DialogContent>
                <DialogHeader><DialogTitle>Richiedi un account</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div>
                    <Label>Nome e cognome</Label>
                    <Input data-testid="reg-name" value={regForm.name} onChange={(e) => setRegForm({ ...regForm, name: e.target.value })} />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input data-testid="reg-email" type="email" value={regForm.email} onChange={(e) => setRegForm({ ...regForm, email: e.target.value })} />
                  </div>
                  <div>
                    <Label>Password (min 8 caratteri)</Label>
                    <Input data-testid="reg-password" type="password" value={regForm.password} onChange={(e) => setRegForm({ ...regForm, password: e.target.value })} />
                  </div>
                  <Button
                    data-testid="reg-submit"
                    onClick={async () => {
                      if (!regForm.email || regForm.password.length < 8) { toast.error("Email + password (min 8 caratteri)"); return; }
                      setRegBusy(true);
                      try {
                        await api.post("/auth/register-request", regForm);
                        toast.success("Richiesta inviata! L'amministratore ti attiverà a breve.");
                        setRegOpen(false); setRegForm({ name: "", email: "", password: "" });
                      } catch (e) {
                        toast.error(e?.response?.data?.detail || "Errore");
                      } finally { setRegBusy(false); }
                    }}
                    disabled={regBusy}
                    className="w-full h-11"
                  >
                    {regBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                    Invia richiesta
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            <Dialog open={forgotOpen} onOpenChange={(o) => { setForgotOpen(o); if (!o) { setForgotStep(1); setForgotForm({ email: "", code: "", new_password: "", new_password2: "" }); } }}>
              <DialogContent>
                <DialogHeader><DialogTitle>Recupera la password</DialogTitle></DialogHeader>
                {forgotStep === 1 ? (
                  <div className="space-y-3">
                    <p className="text-xs text-muted-foreground">
                      Inserisci la tua email: ti invieremo un codice a 6 cifre per impostare una nuova password.
                    </p>
                    <div>
                      <Label>Email</Label>
                      <Input data-testid="forgot-email" type="email" value={forgotForm.email} onChange={(e) => setForgotForm({ ...forgotForm, email: e.target.value })} />
                    </div>
                    <Button
                      data-testid="forgot-submit-1"
                      onClick={async () => {
                        if (!forgotForm.email) { toast.error("Inserisci la tua email"); return; }
                        setForgotBusy(true);
                        try {
                          const { data } = await api.post("/auth/forgot-password", { email: forgotForm.email });
                          toast.success(data?.detail || "Email inviata. Controlla la casella.");
                          setForgotStep(2);
                        } catch (e) {
                          toast.error(e?.response?.data?.detail || "Errore invio email");
                        } finally { setForgotBusy(false); }
                      }}
                      disabled={forgotBusy}
                      className="w-full h-11"
                    >
                      {forgotBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Mail className="h-4 w-4 mr-2" />}
                      Invia codice via email
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <p className="text-xs text-muted-foreground">
                      Se l'email è registrata, abbiamo inviato un codice a <strong>{forgotForm.email}</strong>. Controlla anche la cartella Spam.
                    </p>
                    <div>
                      <Label>Codice (6 cifre)</Label>
                      <Input data-testid="forgot-code" inputMode="numeric" maxLength={6} value={forgotForm.code} onChange={(e) => setForgotForm({ ...forgotForm, code: e.target.value.replace(/\D/g, "") })} className="text-center text-xl tracking-[8px] font-bold" />
                    </div>
                    <div>
                      <Label>Nuova password (min 8 caratteri)</Label>
                      <Input data-testid="forgot-pwd" type="password" value={forgotForm.new_password} onChange={(e) => setForgotForm({ ...forgotForm, new_password: e.target.value })} />
                    </div>
                    <div>
                      <Label>Ripeti nuova password</Label>
                      <Input data-testid="forgot-pwd2" type="password" value={forgotForm.new_password2} onChange={(e) => setForgotForm({ ...forgotForm, new_password2: e.target.value })} />
                    </div>
                    <Button
                      data-testid="forgot-submit-2"
                      onClick={async () => {
                        if (forgotForm.code.length !== 6) { toast.error("Inserisci le 6 cifre del codice"); return; }
                        if (forgotForm.new_password.length < 8) { toast.error("Nuova password minimo 8 caratteri"); return; }
                        if (forgotForm.new_password !== forgotForm.new_password2) { toast.error("Le due password non coincidono"); return; }
                        setForgotBusy(true);
                        try {
                          await api.post("/auth/reset-password", { email: forgotForm.email, code: forgotForm.code, new_password: forgotForm.new_password });
                          toast.success("Password aggiornata! Accedi con la nuova.");
                          setForgotOpen(false); setForgotStep(1); setForgotForm({ email: "", code: "", new_password: "", new_password2: "" });
                        } catch (e) {
                          toast.error(e?.response?.data?.detail || "Errore");
                        } finally { setForgotBusy(false); }
                      }}
                      disabled={forgotBusy}
                      className="w-full h-11"
                    >
                      {forgotBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                      Imposta nuova password
                    </Button>
                    <button type="button" onClick={() => setForgotStep(1)} className="text-xs text-muted-foreground hover:underline w-full text-center">
                      Non hai ricevuto il codice? Rimanda l'email
                    </button>
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
